// Derived store that tracks the overall app lifecycle phase.
// Subscribes to SessionStore and ResourceStore and exposes a single reactive `phase` value
// so components can gate rendering based on what's available.
//
// Phases (in order):
//   BOOT              – App just loaded, nothing has started yet.
//   AUTHENTICATING    – Session fetch / login is in progress.
//   AUTH_FAILED       – Authentication failed (bad credentials, wrong org, backend offline).
//   LOADING_RESOURCES – Authenticated; now fetching grid resources from the API.
//   RESOURCE_FAILED   – Grid resource fetch failed (grid not found, network error).
//   READY             – Fully initialized. Session and resources are available.

import { create } from 'zustand';
import { getSessionStore, useSessionStore, type SessionStatus } from './SessionStore';
import { getResourceStore, useResourceStore, type ResourceStatus } from './ResourceStore';

export enum AppPhase {
    BOOT = 'boot',
    AUTHENTICATING = 'authenticating',
    LOADING_RESOURCES = 'loading-resources',
    READY = 'ready',
    AUTH_FAILED = 'auth-failed',
    RESOURCE_FAILED = 'resource-failed',
}

export enum AuthenticationStatus {
    INITIALIZING = 'initializing',
    NOT_AUTHENTICATED = 'not-authenticated',
    WRONG_ACCOUNT = 'wrong-account',
    AUTHENTICATED = 'authenticated',
    NO_API = 'no-api',
    ERROR = 'error',
}

type AppPhaseStore = {
    phase: AppPhase;
    authStatus: AuthenticationStatus;
    authError: string | null;
    authErrorStatus: number | null;
};

const derivePhase = (sessionStatus: SessionStatus, resourceStatus: ResourceStatus): AppPhase => {
    if (sessionStatus === 'idle') return AppPhase.BOOT;
    if (sessionStatus === 'unauthenticated' || sessionStatus === 'error') return AppPhase.AUTH_FAILED;
    if (sessionStatus === 'authenticated' && resourceStatus === 'idle') return AppPhase.LOADING_RESOURCES;
    if (sessionStatus === 'authenticated' && resourceStatus === 'error') return AppPhase.RESOURCE_FAILED;
    if (sessionStatus === 'authenticated' && resourceStatus === 'loaded') return AppPhase.READY;
    return AppPhase.AUTHENTICATING;
};

const deriveAuthStatus = (
    sessionStatus: SessionStatus,
    resourceStatus: ResourceStatus,
    sessionErrorCode: number | null
): AuthenticationStatus => {
    if (sessionStatus === 'idle' || (sessionStatus === 'authenticated' && resourceStatus === 'idle')) {
        return AuthenticationStatus.INITIALIZING;
    }
    if (sessionStatus === 'unauthenticated') return AuthenticationStatus.NOT_AUTHENTICATED;
    if (sessionStatus === 'error') {
        if (sessionErrorCode === 402) return AuthenticationStatus.ERROR;
        if (sessionErrorCode === 403) return AuthenticationStatus.WRONG_ACCOUNT;
        if (sessionErrorCode === 502) return AuthenticationStatus.NO_API;
        return AuthenticationStatus.ERROR;
    }
    if (resourceStatus === 'error') return AuthenticationStatus.ERROR;
    if (sessionStatus === 'authenticated' && resourceStatus === 'loaded') return AuthenticationStatus.AUTHENTICATED;
    return AuthenticationStatus.INITIALIZING;
};

const deriveAuthError = (
    sessionStatus: SessionStatus,
    resourceStatus: ResourceStatus,
    sessionError: string | null,
    resourceError: string | null
): string | null => {
    if (sessionStatus === 'unauthenticated' || sessionStatus === 'error') return sessionError;
    if (resourceStatus === 'error') return resourceError;
    return null;
};

const deriveAuthErrorStatus = (
    sessionStatus: SessionStatus,
    resourceStatus: ResourceStatus,
    sessionErrorCode: number | null,
    resourceErrorCode: number | null
): number | null => {
    if (sessionStatus === 'unauthenticated' || sessionStatus === 'error') return sessionErrorCode;
    if (resourceStatus === 'error') return resourceErrorCode;
    return null;
};

const computeState = () => {
    const session = getSessionStore();
    const resource = getResourceStore();
    return {
        phase: derivePhase(session.status, resource.status),
        authStatus: deriveAuthStatus(session.status, resource.status, session.errorCode),
        authError: deriveAuthError(session.status, resource.status, session.error, resource.error),
        authErrorStatus: deriveAuthErrorStatus(session.status, resource.status, session.errorCode, resource.errorCode),
    };
};

export const useAppPhaseStore = create<AppPhaseStore>(() => ({
    phase: AppPhase.BOOT,
    authStatus: AuthenticationStatus.INITIALIZING,
    authError: null,
    authErrorStatus: null,
}));

useSessionStore.subscribe(() => {
    useAppPhaseStore.setState(computeState());
});

useResourceStore.subscribe(() => {
    useAppPhaseStore.setState(computeState());
});

export const getAppPhase = () => useAppPhaseStore.getState().phase;
